import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    vapidPublicKey: String,
    subscribeUrl: String,
    unsubscribeUrl: String,
    verifyUrl: String,
    serviceWorkerUrl: { type: String, default: '/manage-service-worker.js' },
    statusMessages: { type: Object, default: {} }
  }
  static targets = ["enableButton", "disableButton", "status", "wrapper"]
  
  connect() {
    console.log('Push notifications controller connected')

    if (!('serviceWorker' in navigator)) {
      console.error('Service Worker not supported')
      this.updateStatus('not-supported')
      return
    }

    if (!('PushManager' in window)) {
      console.error('Push API not supported')
      this.updateStatus('not-supported')
      return
    }

    // Check if we're on HTTPS or localhost
    if (location.protocol !== 'https:' && location.hostname !== 'localhost') {
      console.error('Push notifications require HTTPS')
      this.updateStatus('https-required')
      return
    }

    this.checkSubscription()

    // Add visibility change listener to check subscription when user returns to page
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this)
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
  }

  disconnect() {
    // Clean up visibility change listener
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
  }

  handleVisibilityChange() {
    if (!document.hidden) {
      console.log('Page visible, checking subscription...')
      this.checkSubscription()
    }
  }
  
  async checkSubscription() {
    try {
      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.getSubscription()

      if (subscription) {
        // Verify subscription is still valid on backend
        const isValid = await this.verifySubscriptionOnServer(subscription)

        if (isValid) {
          console.log('Subscription is valid')
          this.updateStatus('enabled')
        } else {
          console.log('Subscription expired, re-subscribing automatically...')
          // Unsubscribe old subscription
          await subscription.unsubscribe()
          // Auto re-subscribe
          await this.enable()
        }
      } else {
        this.updateStatus('disabled')
      }
    } catch (error) {
      console.error('Error checking subscription:', error)
      this.updateStatus('error')
    }
  }

  async verifySubscriptionOnServer(subscription) {
    try {
      const response = await fetch(this.verifyUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({
          endpoint: subscription.endpoint
        })
      })

      if (response.ok) {
        const data = await response.json()
        return data.valid
      }
      return false
    } catch (error) {
      console.error('Error verifying subscription:', error)
      return false
    }
  }
  
  async enable() {
    console.log('Enabling push notifications...')
    try {
      // Register service worker
      console.log('Registering service worker...')
      const registration = await navigator.serviceWorker.register(this.serviceWorkerUrlValue)
      await registration.update()
      console.log('Service worker registered:', registration)
      
      // Request permission
      console.log('Requesting notification permission...')
      const permission = await Notification.requestPermission()
      console.log('Permission result:', permission)
      
      if (permission !== 'granted') {
        this.updateStatus('denied')
        return
      }
      
      // Subscribe to push
      console.log('Subscribing to push notifications...')
      console.log('VAPID public key:', this.vapidPublicKeyValue)
      
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(this.vapidPublicKeyValue)
      })
      console.log('Push subscription created:', subscription)
      
      // Send subscription to server
      const response = await fetch(this.subscribeUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({
          subscription: subscription.toJSON()
        })
      })
      
      if (response.ok) {
        this.updateStatus('enabled')
      } else {
        throw new Error('Failed to save subscription')
      }
    } catch (error) {
      console.error('Error enabling notifications:', error)
      this.updateStatus('error')
    }
  }
  
  async disable() {
    try {
      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.getSubscription()
      
      if (subscription) {
        // Unsubscribe from push
        await subscription.unsubscribe()
        
        // Remove from server
        await fetch(this.unsubscribeUrlValue, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
          },
          body: JSON.stringify({
            endpoint: subscription.endpoint
          })
        })
      }
      
      this.updateStatus('disabled')
    } catch (error) {
      console.error('Error disabling notifications:', error)
      this.updateStatus('error')
    }
  }
  
  updateStatus(status) {
    console.log('Status update:', status)

    // Optional wrapper (onboarding card): hide the whole block when push can
    // never work here, and expose the status for CSS-driven state styling.
    if (this.hasWrapperTarget) {
      this.wrapperTarget.hidden = ['not-supported', 'https-required'].includes(status)
      this.wrapperTarget.dataset.pushStatus = status
    }

    // Update button visibility
    if (this.hasEnableButtonTarget) {
      this.enableButtonTarget.style.display = status === 'disabled' ? 'inline-flex' : 'none'
    }
    if (this.hasDisableButtonTarget) {
      this.disableButtonTarget.style.display = status === 'enabled' ? 'inline-flex' : 'none'
    }
    
    // Update status text if present
    if (this.hasStatusTarget) {
      const messages = {
        'enabled': 'Push notifications enabled',
        'disabled': 'Push notifications disabled',
        'denied': 'Notification permission denied',
        'not-supported': 'Push notifications not supported',
        'https-required': 'HTTPS required for push notifications',
        'error': 'Error with push notifications',
        ...this.statusMessagesValue
      }
      this.statusTarget.textContent = messages[status] || ''
    }
  }
  
  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4)
    const base64 = (base64String + padding).replace(/\-/g, '+').replace(/_/g, '/')
    const rawData = window.atob(base64)
    const outputArray = new Uint8Array(rawData.length)
    
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i)
    }
    return outputArray
  }
}